import { useState } from "react"
import { func, arrayOf, shape } from "prop-types"
import { Button, StackView, Box, Divider, Tooltip } from "@planningcenter/tapestry-react"
import { Select, Checkbox } from "components/ui"
import TextEditor from "components/admin/TextEditor"
import { isBlank, isPresent, arrayToObject } from "utils/helpers"

const RemindersForm = ({ reminders, attendeeTypes, onFieldChange, onChange }) => {
  const [emailFor, setEmailFor] = useState(
    reminders.every(r => r.attendeeTypes.length === 0) ? "all" : "certain"
  )

  const [customMessage, setCustomMessage] = useState(
    arrayToObject(
      reminders.map(r => ({ key: r.key, useCustomMessage: isPresent(r.message) })),
      "key"
    )
  )

  const selectedAttendeeTypes = reminders.reduce((acc, r) => [...acc, ...r.attendeeTypes], [])

  const handleEmailForAll = (reminder, value) => {
    setEmailFor(value)

    onChange({
      ...reminder,
      attendeeTypes: []
    })
  }

  const handleEmailForChange = (reminder, { target: { value } }) => {
    if (value === "certain") return setEmailFor(value)
    if (value === "all" && reminders.length === 1) return handleEmailForAll(reminder, value)

    return alertModal(
      {
        title: "Remove other reminders?",
        text:
          "Changing back to all attendees will remove all selected attendee types for this reminder. Are you sure?",
        showCancelButton: true,
        cancelButtonText: "Cancel",
        confirmButtonText: "Confirm",
        customClass: "destructive-warning",
        closeOnConfirm: true
      },
      () => handleEmailForAll(reminder, value)
    )
  }

  const handleAttendeeTypeChange = (reminder, { target: { value } }) => {
    const attendeeTypeId = Number(value)

    const reminderAttendeeTypes = reminder.attendeeTypes.includes(attendeeTypeId)
      ? reminder.attendeeTypes.filter(id => id !== attendeeTypeId)
      : [...reminder.attendeeTypes, attendeeTypeId]

    handleChange(reminder, "attendeeTypes", {
      target: { value: reminderAttendeeTypes }
    })
  }

  const handleChange = (reminder, field, { target: { value } }) => {
    const newValue = field === "durationNumber" ? value.replace(/\D/g, "") : value

    onFieldChange({
      ...reminder,
      [field]: newValue
    })
  }

  const handleCustomMessageChange = (reminder, { target: { checked } }) => {
    setCustomMessage({
      ...customMessage,
      [reminder.key]: { ...customMessage[reminder.key], useCustomMessage: checked }
    })

    handleChange(reminder, "message", { target: { value: "" } })
  }

  const handleAddReminder = () => {
    const lastReminder = reminders[reminders.length - 1]

    const newReminder = {
      key: Date.now(),
      durationNumber: lastReminder.durationNumber,
      durationUnit: lastReminder.durationUnit,
      durationUnitOptions: lastReminder.durationUnitOptions,
      message: null,
      attendeeTypes: [],
      attendeeTypeNames: ""
    }

    onChange(newReminder, "new")
  }

  const handleRemoveReminder = reminder => onChange(reminder, "delete")

  return (
    <div>
      {reminders.map(reminder => (
        <StackView key={reminder.key} spacing={2} marginTop={2} marginBottom={2}>
          <StackView axis="horizontal" alignment="center" spacing={1} flex="1">
            Email
            <Select
              style={{ width: "fit-content" }}
              value={emailFor}
              name="emailFor"
              options={[
                { value: "all", label: "all attendees" },
                { value: "certain", label: "only certain attendees" }
              ]}
              onChange={v => handleEmailForChange(reminder, v)}
            />
            <input
              type="number"
              style={{ width: "64px" }}
              name={`${reminder.key}_durationNumber`}
              value={reminder.durationNumber}
              onChange={v => handleChange(reminder, "durationNumber", v)}
            />
            <Select
              style={{ width: "fit-content" }}
              value={reminder.durationUnit}
              name={`${reminder.key}_durationUnit`}
              options={reminder.durationUnitOptions.map(option => {
                const value = reminder.durationNumber != 1 ? option : option.slice(0, -1)

                return {
                  value: option,
                  label: option !== "minutes_from_now" ? value : "minutes from now"
                }
              })}
              onChange={v => handleChange(reminder, "durationUnit", v)}
            />
            {reminder.durationUnit !== "minutes_from_now" && "before event."}
          </StackView>

          {emailFor === "certain" && (
            <Box flex="1" marginLeft="48px">
              {attendeeTypes.map(attendeeType => (
                <Tooltip
                  key={attendeeType.id}
                  placement="top"
                  title={
                    !reminder.attendeeTypes.includes(attendeeType.id) &&
                    selectedAttendeeTypes.includes(attendeeType.id) ? (
                      <>
                        <div>{attendeeType.name} cannot be included in another reminder.</div>
                        <div>Only 1 reminder is allowed per selection type.</div>
                      </>
                    ) : (
                      ""
                    )
                  }
                >
                  <Box width="fit-content">
                    <Checkbox
                      name={`${reminder.key}_${attendeeType.id}`}
                      htmlFor={`${reminder.key}_${attendeeType.id}`}
                      text={attendeeType.name}
                      value={attendeeType.id.toString()}
                      checked={reminder.attendeeTypes.includes(attendeeType.id)}
                      onSelect={v => handleAttendeeTypeChange(reminder, v)}
                      disabled={
                        !reminder.attendeeTypes.includes(attendeeType.id) &&
                        selectedAttendeeTypes.includes(attendeeType.id)
                      }
                    />
                  </Box>
                </Tooltip>
              ))}
            </Box>
          )}

          <Box flex="1">
            <Checkbox
              name={`${reminder.key}_customMessage`}
              htmlFor={`${reminder.key}_customMessage`}
              text="Include custom message"
              checked={customMessage[reminder.key]?.useCustomMessage || false}
              onSelect={v => handleCustomMessageChange(reminder, v)}
            />
            {customMessage[reminder.key]?.useCustomMessage && (
              <TextEditor
                name={`${reminder.key}_message`}
                value={reminder.message}
                labelRowClasses="d-f jc-sb ai-c"
                onChange={v => handleChange(reminder, "message", v)}
              />
            )}
          </Box>

          {emailFor === "certain" && reminders.length > 1 && (
            <Box>
              <Button
                theme="destroy"
                title="Remove reminder"
                variant="outline"
                size="sm"
                onClick={() => handleRemoveReminder(reminder)}
              />
              <Divider
                css={{ marginTop: "1rem", width: "calc(100% + 2rem)", marginLeft: "-1rem" }}
              />
            </Box>
          )}
        </StackView>
      ))}

      {emailFor === "certain" && (
        <Box>
          <Button
            variant="outline"
            title="Add another reminder"
            onClick={handleAddReminder}
            disabled={
              reminders.some(reminder => isBlank(reminder.attendeeTypes)) ||
              selectedAttendeeTypes.length === attendeeTypes.length
            }
          />
        </Box>
      )}
    </div>
  )
}

RemindersForm.propTypes = {
  reminders: arrayOf(shape({})).isRequired,
  attendeeTypes: arrayOf(shape({})).isRequired,
  onChange: func.isRequired,
  onFieldChange: func.isRequired
}

export default RemindersForm
